import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import {
    ExecutiveSummaryOverview,
    ComprehensiveResearchSection,
    ValidationResultsSection,
    ConfidenceDashboardSection,
    CostAnalysisSection,
    ProgressiveStackEvolution
} from '../components/AnalysisDisplay.jsx';
import {
    LogoText,
    Button,
    Heading,
    Text,
    Container,
    Card,
    Badge,
    Icon,
    NavLink
} from '../components/ui/index.jsx';
import AppFlowDiagram from '../components/AppFlowDiagram.jsx';

// Sample Diagram Data for "Visual Architecture" tab
const SAMPLE_DIAGRAM_DATA = {
    userJourney: `journey
    title User Experience Flow
    section Onboarding
      Sign Up: 5: User
      Select Plan: 4: User
    section Creation
      Input Logic: 5: User
      Generate App: 5: System
    section Deploy
      Review Code: 4: User
      One-Click Deploy: 5: User`,
    systemArchitecture: `flowchart TD
    Client[📱 Client App] -->|HTTPS/WSS| CDN[☁️ Global CDN]
    CDN --> LB[⚖️ Load Balancer]
    LB --> API[🚀 API Gateway]
    
    subgraph Services
    API --> Auth[🔐 Auth Service]
    API --> Core[🧠 AI Logic Core]
    API --> Pay[💳 Billing Service]
    end
    
    subgraph Data
    Core --> DB[(🗄️ Primary DB)]
    Core --> Cache[(⚡ Redis Cache)]
    Core --> Vector[(🔍 Vector DB)]
    end`,
    systemFlow: `flowchart LR
    Start([🚀 User Request]) --> Validate{✅ Valid?}
    Validate -->|No| Error[⚠️ Return 400]
    Validate -->|Yes| Process[⚙️ Process Data]
    Process --> AI[🧠 Call AI Model]
    AI --> Save[(💾 Save to DB)]
    Save --> Notify[🔔 Send Notification]
    Notify --> End([✨ Success Response])`,
    authFlow: `sequenceDiagram
    participant User as 👤 User
    participant Client as 📱 App
    participant Auth as 🔐 Auth0/Cognito
    participant API as 🚀 API Gateway
    
    User->>Client: Click Login
    Client->>Auth: Request Token
    Auth-->>User: Show Mock Login UI
    User->>Auth: Enter Credentials
    Auth-->>Client: Returns JWT Token
    Client->>API: API Call + Bearer Token
    API-->>Client: Protected Data`,
    deploymentPipeline: `flowchart TD
    Push[💻 Git Push] --> CI[⚙️ CI Pipeline]
    CI --> Test[🧪 Run Tests]
    Test --> Build[📦 Build Container]
    Build --> Scan[🔒 Security Scan]
    Scan --> Deploy[🚀 Deploy to Staging]
    Deploy --> Verify{✅ Verified?}
    Verify -->|Yes| Prod[🌍 Promote to Prod]
    Verify -->|No| Rollback[↩️ Rollback]`
};

const TIME_TRACKER_DATA = {
    overview: {
        projectName: "Smart Time Tracker",
        confidence: 95,
        stats: [
            { icon: "clock", value: "3-4 Weeks", label: "Implementation Time" },
            { icon: "money", value: "$0/month", label: "Free Tier Optimized" },
            { icon: "layers", value: "Web, Mobile", label: "Target Platform" }
        ],
        solutionOverview: "A comprehensive time tracking solution that enables teams and individuals to monitor productivity across web and mobile platforms with real-time synchronization, featuring an intuitive admin dashboard for user management and detailed analytics. Built with modern technologies like Next.js and Supabase, it provides offline-first mobile experience, role-based access control, and seamless data export capabilities while maintaining cost-effectiveness through generous free tiers.",
        requirements: {
            where: "web, mobile",
            who: "public",
            auth: "email-password",
            stage: "development"
        },
        coreFeatures: [
            "Real-time time tracking with start/stop timers",
            "Admin dashboard with user management and analytics",
            "Cross-platform mobile app with offline support",
            "Project and task organization",
            "Comprehensive reporting and data export",
            "Role-based access control and team management"
        ],
        architecture: {
            frontend: "Next.js 14 with React - SSR, API routes, optimal SEO",
            backend: "Supabase - PostgreSQL, REST API, Realtime, Auth (100K MAU free)",
            database: "Neon PostgreSQL - 3GB free storage, better for time-series growth",
            authentication: "Supabase Auth - 100K MAU free, email/password, social login",
            deployment: "Vercel - unlimited deployments, serverless functions, global CDN",
            cdn: "Vercel Edge Network - included with hosting",
            additionalServices: "Expo for React Native, Upstash Redis for caching, Resend for emails, OneSignal for push notifications"
        },
        validation: {
            limits: [
                { service: "Supabase Auth", limit: "100K MAU free" },
                { service: "Vercel Hosting", limit: "100GB bandwidth, unlimited deployments" },
                { service: "Neon DB", limit: "3GB storage, 1 compute hour free" },
                { service: "Resend", limit: "3K emails/month free" },
                { service: "Upstash Redis", limit: "10K requests/day free" },
                { service: "Expo", limit: "Free builds and OTA updates" }
            ],
            alternatives: [
                { name: "Supabase Auth", insteadOf: "Clerk", reason: "100K MAU free vs 10K, seamless integration" },
                { name: "Neon PostgreSQL", insteadOf: "Supabase Postgres", reason: "3GB free storage vs 500MB, time-series growth" },
                { name: "Expo", insteadOf: "Manual React Native", reason: "Free build services, OTA updates, simplified deployment" }
            ],
            estimatedValue: "$150-200 if all services were paid"
        },
        challenges: [
            "Real-time synchronization across web and mobile platforms",
            "Efficient time-series data storage with millisecond precision",
            "Offline-first mobile experience with sync capabilities",
            "Scalable architecture for concurrent timer sessions",
            "Role-based access control implementation"
        ],
        metrics: {
            servicesUsed: 8,
            monthlyValue: "$150 (free)",
            firstUpgrade: "When exceeding 10,000 MAU or 500MB database",
            upgradeCost: "$25/month"
        },
        status: [
            "Architecture validated",
            "Free services selected",
            "Tech stack optimized",
            "Ready for implementation"
        ]
    },

    originalIdea: "A smart time tracking app for freelancers that automatically categorizes work based on active window titles and GPS location.",
    platform: "Web & Mobile",

    // Research Phase Data
    research: {
        techStack: {
            frontend: "React Native (Expo)",
            backend: "Node.js (Express)",
            database: "MongoDB Atlas",
            authentication: "Auth0",
            deployment: "Heroku",
            analytics: "Google Analytics"
        },
        researchSources: [
            { title: "React Native Performance Guide", url: "https://reactnative.dev/docs/performance", type: "documentation", relevance: "High" },
            { title: "MongoDB Atlas Free Tier Limits", url: "https://www.mongodb.com/pricing", type: "pricing", relevance: "Critical" },
            { title: "Building Scalable Time Trackers", url: "#", type: "article", relevance: "Medium" },
            { title: "Auth0 vs Supabase for Auth", url: "#", type: "comparison", relevance: "High" }
        ],
        researchedServices: {
            totalFreeServices: 5,
            currentFreeLimits: {
                database: "512MB Storage",
                authentication: "7,000 active users",
                hosting: "550 dyno hours/mo",
                analytics: "10M events/mo"
            },
            discoveredViaAwesomeLists: ["React Native", "Expo", "Express"]
        },
        estimatedCost: {
            monthly: "$0",
            freeServicesUsed: 5,
            firstPaidUpgrade: "Database scaling (>512MB)"
        },
        reasoning: "Initial stack focused on popular, well-documented technologies with decent free tiers. MongoDB Atlas chosen for flexible data schema suitable for time logs.",
        challenges: [
            "Battery drain from continuous GPS tracking",
            "Privacy concerns with window title logging",
            "Offline data synchronization"
        ],
        coreFeatures: [
            "Automatic activity detection",
            "GPS-based location tagging",
            "Manual time entry correction",
            "Weekly productivity reports",
            "Invoice generation"
        ],
        estimatedTimeframe: "3-4 weeks",
        projectType: "Full Stack App"
    },

    // Validation Phase Data
    validation: {
        validation: {
            isValid: true,
            improvements: [
                "Switch Auth0 to Supabase Auth for better pricing at scale",
                "Replace MongoDB with PostgreSQL (Supabase) for relational data integrity",
                "Use Vercel instead of Heroku for better static serving and free tier"
            ],
            concerns: [
                "Heroku free tier is deprecated/limited",
                "MongoDB relational data handling for invoices might get complex"
            ],
            recommendations: "Transition to a fully Supabase-backed stack (Auth, DB, Realtime) to unify services and reduce cost."
        },
        betterFreeAlternatives: [
            { "instead-of": "Auth0", use: "Supabase Auth", reason: "Unlimited MAU on free tier vs 7k limit" },
            { "instead-of": "MongoDB", use: "Supabase (PostgreSQL)", reason: "Better for relational data (Projects -> Tasks -> Logs)" },
            { "instead-of": "Heroku", use: "Vercel", reason: "Generous free tier for frontend & serverless functions" }
        ],
        refinedArchitecture: {
            techStack: {
                frontend: "React Native (Expo)",
                backend: "Supabase Edge Functions",
                database: "Supabase (PostgreSQL)",
                authentication: "Supabase Auth",
                deployment: "Vercel",
                storage: "Supabase Storage"
            }
        },
        improvedArchitecture: {
            validatedFreeLimits: {
                breakdown: {
                    database: "500MB database",
                    auth: "50,000 monthly active users",
                    storage: "1GB file storage",
                    transfer: "5GB bandwidth"
                },
                totalMonthlyValue: "$120"
            },
            freeServicesUsed: 6,
            firstUpgradeTrigger: "Database > 500MB",
            upgradeCost: "$25/month",
            reasoning: "Supabase offers a unified 'Backend-as-a-Service' that replaces Auth0, MongoDB, and dedicated backend hosting, significantly simplifying the stack and increasing free tier limits."
        }
    },

    // Technical Tab Data
    technical: [
        {
            title: "Presentation Layer",
            icon: "layout",
            tech: "React Native",
            description: "Cross-platform mobile UI with native performance.",
            stats: [
                { label: "Components", value: "24" },
                { label: "Screens", value: "8" },
                { label: "Shared", value: "95%" }
            ],
            structure: [
                "src/components/TrackerUI.tsx",
                "src/screens/Dashboard.tsx",
                "src/navigation/AppNavigator.tsx"
            ]
        },
        {
            title: "Data Layer",
            icon: "database",
            tech: "Supabase (PostgreSQL)",
            description: "Relational data store with real-time subscriptions.",
            stats: [
                { label: "Tables", value: "6" },
                { label: "Relations", value: "12" },
                { label: "Triggers", value: "4" }
            ],
            structure: [
                "supabase/migrations/01_init.sql",
                "src/lib/database.types.ts",
                "src/hooks/useRealtime.ts"
            ]
        },
        {
            title: "Business Logic",
            icon: "cpu",
            tech: "Edge Functions",
            description: "Serverless functions for complex calculations.",
            stats: [
                { label: "Functions", value: "5" },
                { label: "Avg Latency", value: "45ms" },
                { label: "Region", value: "Edge" }
            ],
            structure: [
                "supabase/functions/process-logs/index.ts",
                "supabase/functions/generate-invoice/index.ts"
            ]
        }
    ],

    // Prompts Tab Data
    prompts: [
        {
            id: 1,
            tag: "Setup",
            title: "Initial Project Scaffold",
            whyMatters: "Sets up the core structure, dependencies, and configuration for the React Native + Supabase stack.",
            expectedOutputs: ["Initialized Expo project", "Supabase client setup", "Type definitions"],
            successValidation: ["App runs on simulator", "Connects to Supabase"],
            code: "Create a new React Native Expo project using TypeScript. Initialize Supabase client with environment variables. Configure navigation using Expo Router."
        },
        {
            id: 2,
            tag: "Database",
            title: "Schema & Migrations",
            whyMatters: "Defines the data model for Users, Projects, and TimeLogs, ensuring relational integrity.",
            expectedOutputs: ["SQL Migration file", "RLS Policies", "Table definitions"],
            successValidation: ["Tables created in dashboard", "RLS enforces user privacy"],
            code: "Generate SQL for 'profiles', 'projects', and 'time_logs' tables. Set up Row Level Security (RLS) so users can only access their own data. Create foreign key relationships."
        },
        {
            id: 3,
            tag: "Feature",
            title: "Geolocation Tracking",
            whyMatters: "Implements the core value proposition: tracking time based on location.",
            expectedOutputs: ["Background location task", "Coordinate storage logic", "Permission handling"],
            successValidation: ["Tracks location in background", "Updates log entry"],
            code: "Implement a background task using Expo Location that triggers when the user enters specific geofenced regions. Store entry/exit times in the 'time_logs' table."
        }
    ]
};

export default function HowItWorks() {
    const navigate = useNavigate();
    const [tourTab, setTourTab] = React.useState('overview');
    const [evolutionStep, setEvolutionStep] = React.useState(0);
    const [copiedPrompt, setCopiedPrompt] = React.useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

    const TABS = [
        { id: 'overview', label: 'Overview', icon: 'zap' },
        { id: 'evolution', label: 'Evolution', icon: 'gitBranch' },
        { id: 'technical', label: 'Technical', icon: 'code' },
        { id: 'flow', label: 'Flow Diagrams', icon: 'layout' },
        { id: 'prompts', label: 'AI Prompts', icon: 'terminal' }
    ];

    const handleCopy = (id) => {
        setCopiedPrompt(id);
        setTimeout(() => setCopiedPrompt(null), 2000);
    };

    return (
        <div className="min-h-screen text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1">
                    <Container size="6xl">
                        {/* Hero Section */}
                        <div className="py-16 md:py-24 text-center px-4 relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-[#7B61FF]/10 to-[#FF00FF]/10 blur-[100px] rounded-full pointer-events-none"></div>

                            <Heading level={1} className="mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                                How It Works
                            </Heading>
                            <div className="w-24 h-1 bg-[#7B61FF] mx-auto rounded-full shadow-[0_0_10px_#7B61FF] mb-8"></div>

                            <Text variant="secondary" size="lg" className="max-w-2xl mx-auto leading-relaxed text-gray-300 mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                                Stop guessing. Start building. The Architect acts as your AI Chief Technology Officer, planning every detail—from <strong className="text-white font-medium">cloud infrastructure and hosting</strong> to API security—before you write a single line of code.
                            </Text>

                        </div>

                        {/* The Problem vs Solution Section */}
                        <section className="py-20 mb-16 relative">
                            <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
                                {/* The Old Way */}
                                <div className="glass-panel p-8 rounded-2xl border-white/5 bg-black/20 transform md:rotate-[-2deg] opacity-90 hover:opacity-100 transition-all duration-500 hover:rotate-0 hover:scale-105 hover:z-10 group">
                                    <div className="flex items-center mb-6">
                                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 mr-4">
                                            <Icon name="map" size="sm" className="text-blue-400" />
                                        </div>
                                        <Heading level={4} className="text-blue-200">Strategic Foundation</Heading>
                                    </div>
                                    <ul className="space-y-4">
                                        {[
                                            "Plan before you execution",
                                            "Control your own infrastructure",
                                            "Built to grow with your business",
                                            "Do it right the first time",
                                            "Own every line of code"
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start text-gray-400 group-hover:text-blue-200/90 transition-colors">
                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400/40 mt-2 mr-3 flex-shrink-0"></span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* The Architect Way */}
                                <div className="glass-panel p-8 rounded-2xl border-[#7B61FF]/30 bg-[#7B61FF]/5 transform md:rotate-[2deg] shadow-[0_0_40px_rgba(123,97,255,0.1)] hover:rotate-0 hover:scale-105 transition-all duration-500 z-10">
                                    <div className="flex items-center mb-6">
                                        <div className="w-10 h-10 rounded-full bg-[#7B61FF]/20 flex items-center justify-center border border-[#7B61FF]/40 mr-4">
                                            <Icon name="check" size="sm" className="text-[#BA87FF]" />
                                        </div>
                                        <Heading level={4} className="text-[#BA87FF]">The Architect Way</Heading>
                                    </div>
                                    <ul className="space-y-4">
                                        {[
                                            "Precise, technical blueprints",
                                            "Security & best practices built-in",
                                            "Planned for scale from Day 1",
                                            "Full Code Ownership (Zero Lock-In)",
                                            "Hosting & Deployment Strategy"
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start text-gray-200">
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#7B61FF] shadow-[0_0_8px_#7B61FF] mt-2 mr-3 flex-shrink-0"></span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* How It Works - Visual Process */}
                        <section className="py-20 mb-20">
                            <div className="text-center mb-16">
                                <Badge variant="primary" className="mb-4">The Process</Badge>
                                <Heading level={2} className="mb-4">From Chaos to Clarity</Heading>
                                <Text variant="secondary">Four steps to professional software implementation.</Text>
                            </div>

                            <div className="relative">
                                {/* Connecting Line */}
                                <div className="hidden md:block absolute top-[2.5rem] left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-[#7B61FF]/0 via-[#7B61FF]/40 to-[#7B61FF]/0 z-0"></div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
                                    {[
                                        {
                                            step: "01",
                                            title: "Input Idea",
                                            desc: "Describe your dream app in plain English. No technical jargon needed.",
                                            icon: "lightbulb"
                                        },
                                        {
                                            step: "02",
                                            title: "AI Analysis",
                                            desc: "Deep analysis defines your hosting stack, data models, APIs, and logic.",
                                            icon: "cpu" // mapped to existing, or use fallback
                                        },
                                        {
                                            step: "03",
                                            title: "The Blueprint",
                                            desc: "Receive a comprehensive implementation plan.",
                                            icon: "fileText"
                                        },
                                        {
                                            step: "04",
                                            title: "Build",
                                            desc: "Copy-paste specific micro-prompts into your favorite AI coder.",
                                            icon: "code"
                                        }
                                    ].map((item, index) => (
                                        <div key={index} className="flex flex-col items-center text-center group">
                                            <div className="w-20 h-20 rounded-2xl glass-panel border border-[#7B61FF]/20 flex items-center justify-center mb-6 relative transition-all duration-300 group-hover:scale-110 group-hover:border-[#7B61FF]/50 group-hover:shadow-[0_0_20px_rgba(123,97,255,0.3)] bg-[#0A0A0A]">
                                                <Text className="text-2xl font-bold text-[#7B61FF] group-hover:text-white transition-colors absolute top-2 right-3 opacity-50 text-xs">{item.step}</Text>
                                                <Icon name={item.icon} size="xl" className="text-[#7B61FF] group-hover:text-white transition-colors" />
                                                {/* Pulse Effect for current step visualization */}
                                                <div className="absolute inset-0 rounded-2xl bg-[#7B61FF]/10 animate-ping opacity-0 group-hover:opacity-100 transition-opacity duration-1000" style={{ animationDuration: '2s' }}></div>
                                            </div>
                                            <Heading level={4} className="mb-3 text-lg font-bold">{item.title}</Heading>
                                            <Text variant="secondary" size="sm" className="leading-relaxed px-2">{item.desc}</Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* The "Magic" Reveal Section - Mock UI */}
                        <section className="py-20 mb-20">
                            <div className="glass-panel p-1 rounded-2xl border border-white/10 bg-black/40 overflow-hidden relative shadow-2xl">
                                {/* Top Bar Mockup */}
                                <div className="bg-[#1A1A1A] px-4 py-3 border-b border-white/5 flex items-center gap-2">
                                    <div className="flex gap-2 mr-4">
                                        <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
                                    </div>
                                    <div className="h-6 w-96 bg-white/5 rounded-md flex items-center px-3">
                                        <span className="text-[10px] text-gray-500">the-architect-blueprint-generator.tsx</span>
                                    </div>
                                </div>

                                {/* Content Mockup */}
                                <div className="p-8 md:p-12 relative">
                                    <div className="absolute top-0 right-0 p-8 opacity-20 pointer-events-none">
                                        <div className="w-64 h-64 bg-[#7B61FF] rounded-full blur-[100px]"></div>
                                    </div>

                                    {/* Tab Switcher */}
                                    {/* Tour Container - Mimics the Real App */}
                                    <div className="glass-panel rounded-xl overflow-hidden border border-[#7B61FF]/30 bg-[#0F1117] shadow-2xl relative">
                                        {/* App Header */}
                                        <div className="bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-4 border-b border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="bg-[#7B61FF]/20 p-2 rounded-lg">
                                                    <div className="w-5 h-5 rounded-full border-2 border-[#BA87FF] border-t-transparent animate-spin-slow"></div>
                                                </div>
                                                <div>
                                                    <div className="text-sm text-gray-400">Project Analysis</div>
                                                    <div className="font-semibold text-white">Smart Time Tracker</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Badge variant="success" className="bg-green-500/10 text-green-400 border-green-500/20">
                                                    <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                                                    Ready to Build
                                                </Badge>
                                            </div>
                                        </div>

                                        {/* Tour Navigation Tabs */}
                                        {/* Tour Navigation Tabs */}
                                        <div className="mb-8 md:mb-12 px-4 md:px-0">
                                            {/* Mobile Navigation - Visible Grid */}
                                            <div className="md:hidden grid grid-cols-2 gap-2 px-4 mb-4">
                                                {TABS.map((tab, idx) => (
                                                    <button
                                                        key={tab.id}
                                                        onClick={() => setTourTab(tab.id)}
                                                        className={`
                                                            p-3 rounded-xl text-xs font-bold transition-all flex flex-col items-center justify-center gap-2 border min-h-[80px]
                                                            ${tourTab === tab.id
                                                                ? 'bg-[#7B61FF] border-[#7B61FF] text-white shadow-lg'
                                                                : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:bg-white/10'}
                                                            ${idx === TABS.length - 1 && TABS.length % 2 !== 0 ? 'col-span-2' : ''} 
                                                        `}
                                                    >
                                                        <Icon name={tab.icon} size="md" className={tourTab === tab.id ? 'text-white' : 'text-[#7B61FF]'} />
                                                        {tab.label}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Desktop Tabs */}
                                            <div className="hidden md:flex justify-center">
                                                <div className="inline-flex bg-white/5 p-1 rounded-xl border border-white/10 relative whitespace-nowrap">
                                                    {TABS.map((tab) => (
                                                        <button
                                                            key={tab.id}
                                                            onClick={() => setTourTab(tab.id)}
                                                            className={`
                                                                    px-5 py-2 md:px-6 md:py-2.5 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 relative z-10 whitespace-nowrap
                                                                    ${tourTab === tab.id ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white'}
                                                                `}
                                                        >
                                                            {tourTab === tab.id && (
                                                                <div className="absolute inset-0 bg-[#7B61FF] rounded-lg -z-10 animate-fade-in"></div>
                                                            )}
                                                            <Icon name={tab.icon} size="sm" />
                                                            {tab.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tour Content Area */}
                                        <div className="p-4 md:p-8 min-h-[400px]">
                                            {/* TAB 1: OVERVIEW */}
                                            {tourTab === 'overview' && (
                                                <div className="space-y-8 animate-fade-in pb-10">
                                                    {/* Hero Header */}
                                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-white/10 pb-6">
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <Badge variant="outline" className="border-[#BA87FF]/30 text-[#BA87FF] text-[10px] uppercase tracking-wider">Production Ready</Badge>
                                                                <Badge variant="outline" className="border-green-500/30 text-green-400 text-[10px] uppercase tracking-wider">AI Validated</Badge>
                                                            </div>
                                                            <Heading level={2} className="text-3xl text-white">{TIME_TRACKER_DATA.overview.projectName}</Heading>
                                                        </div>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-white/5 rounded-lg border border-white/10 p-2">
                                                            {TIME_TRACKER_DATA.overview.stats.map((stat, i) => (
                                                                <div key={i} className="px-2 py-2 bg-black/20 rounded border border-white/5 text-center flex flex-col justify-center">
                                                                    <div className="text-lg font-bold text-white leading-none mb-1">{stat.value}</div>
                                                                    <div className="text-[10px] text-gray-400 uppercase tracking-wide break-words leading-tight">{stat.label}</div>
                                                                </div>
                                                            ))}
                                                            <div className="px-2 py-2 text-center bg-[#7B61FF]/10 rounded border border-[#7B61FF]/20 flex flex-col justify-center">
                                                                <div className="text-lg font-bold text-[#BA87FF] leading-none mb-1">{TIME_TRACKER_DATA.overview.confidence}%</div>
                                                                <div className="text-[10px] text-[#BA87FF]/70 uppercase tracking-wide">Confidence</div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                                        {/* Left Column: Overview, Features, Challenges */}
                                                        <div className="lg:col-span-2 space-y-8">
                                                            {/* Solution Overview */}
                                                            <div>
                                                                <Heading level={4} className="text-white mb-4 flex items-center gap-2">
                                                                    <Icon name="fileText" size="sm" className="text-[#7B61FF]" /> Solution Overview
                                                                </Heading>
                                                                <Text className="text-gray-300 leading-relaxed text-sm bg-white/5 p-6 rounded-2xl border border-white/10">
                                                                    {TIME_TRACKER_DATA.overview.solutionOverview}
                                                                </Text>
                                                            </div>

                                                            {/* Core Features */}
                                                            <div>
                                                                <Heading level={4} className="text-white mb-4 flex items-center gap-2">
                                                                    <Icon name="star" size="sm" className="text-[#7B61FF]" /> Core Features
                                                                </Heading>
                                                                <div className="grid sm:grid-cols-2 gap-3">
                                                                    {TIME_TRACKER_DATA.overview.coreFeatures.map((feature, i) => (
                                                                        <div key={i} className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/5 text-sm text-gray-300">
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-[#7B61FF] shrink-0"></div>
                                                                            {feature}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Recommended Architecture */}
                                                            <div>
                                                                <Heading level={4} className="text-white mb-4 flex items-center gap-2">
                                                                    <Icon name="cpu" size="sm" className="text-[#7B61FF]" /> Recommended Architecture
                                                                </Heading>
                                                                <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5">
                                                                    {Object.entries(TIME_TRACKER_DATA.overview.architecture).map(([key, value]) => (
                                                                        <div key={key} className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
                                                                            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold sm:col-span-1 py-1">{key}</div>
                                                                            <div className="text-sm text-gray-200 sm:col-span-3">{value}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Implementation Status */}
                                                            <div>
                                                                <Heading level={4} className="text-white mb-4 flex items-center gap-2">
                                                                    <Icon name="checkCircle" size="sm" className="text-green-400" /> Implementation Status
                                                                </Heading>
                                                                <div className="flex flex-wrap gap-4">
                                                                    {TIME_TRACKER_DATA.overview.status.map((item, i) => (
                                                                        <Badge key={i} variant="success" className="bg-green-500/10 text-green-400 border-green-500/30 px-3 py-1.5 h-auto">
                                                                            <span className="mr-2">✓</span> {item}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Right Column: Requirements, Metrics, Validation */}
                                                        <div className="space-y-6">
                                                            {/* Project Requirements Card */}
                                                            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-5">
                                                                <Heading level={4} className="text-white text-sm uppercase tracking-wider mb-4 border-b border-white/10 pb-2">User Requirements</Heading>
                                                                <div className="space-y-4">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-gray-500">Platform</span>
                                                                        <span className="text-sm text-white font-medium">{TIME_TRACKER_DATA.overview.requirements.where}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-gray-500">Target Audience</span>
                                                                        <span className="text-sm text-white font-medium">{TIME_TRACKER_DATA.overview.requirements.who}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-gray-500">Auth Method</span>
                                                                        <span className="text-sm text-white font-medium">{TIME_TRACKER_DATA.overview.requirements.auth}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-gray-500">Stage</span>
                                                                        <span className="text-sm text-white font-medium uppercase">{TIME_TRACKER_DATA.overview.requirements.stage}</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Validated Free Limits */}
                                                            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-5">
                                                                <Heading level={4} className="text-white text-sm uppercase tracking-wider mb-4 border-b border-white/10 pb-2">Validated Free Limits</Heading>
                                                                <div className="space-y-3">
                                                                    {TIME_TRACKER_DATA.overview.validation.limits.map((limit, i) => (
                                                                        <div key={i} className="text-sm">
                                                                            <div className="text-[#BA87FF] text-xs font-semibold mb-0.5">{limit.service}</div>
                                                                            <div className="text-gray-400">{limit.limit}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                                                                    <span className="text-xs text-gray-500">Monthly Value</span>
                                                                    <span className="text-green-400 font-bold">{TIME_TRACKER_DATA.overview.metrics.monthlyValue}</span>
                                                                </div>
                                                            </div>

                                                            {/* Alternatives Applied */}
                                                            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-5">
                                                                <Heading level={4} className="text-white text-sm uppercase tracking-wider mb-4 border-b border-white/10 pb-2">Alternatives Applied</Heading>
                                                                <div className="space-y-4">
                                                                    {TIME_TRACKER_DATA.overview.validation.alternatives.map((alt, i) => (
                                                                        <div key={i} className="bg-white/5 rounded-lg p-3 text-xs">
                                                                            <div className="flex justify-between mb-1">
                                                                                <span className="text-white font-semibold">{alt.name}</span>
                                                                                <span className="text-gray-600 line-through">{alt.insteadOf}</span>
                                                                            </div>
                                                                            <div className="text-gray-400 italic">"{alt.reason}"</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Challenges */}
                                                            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-5">
                                                                <Heading level={4} className="text-red-300 text-sm uppercase tracking-wider mb-4 border-b border-red-500/10 pb-2">Challenges Addressed</Heading>
                                                                <ul className="space-y-2">
                                                                    {TIME_TRACKER_DATA.overview.challenges.slice(0, 3).map((c, i) => (
                                                                        <li key={i} className="text-xs text-red-200/70 flex items-start gap-2">
                                                                            <span className="mt-0.5">•</span> {c}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* TAB 2: THE EVOLUTION */}
                                            {tourTab === 'evolution' && (
                                                <div className="space-y-8 animate-fade-in text-left">
                                                    <div className="text-center mb-8">
                                                        <Heading level={2} className="text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
                                                            Research & Architecture Evolution
                                                        </Heading>
                                                        <Text variant="secondary" className="max-w-2xl mx-auto">
                                                            See how The Architect refined the initial idea into a production-ready stack
                                                        </Text>
                                                    </div>

                                                    <ProgressiveStackEvolution
                                                        research={TIME_TRACKER_DATA.research}
                                                        validation={TIME_TRACKER_DATA.validation}
                                                    />

                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                        <ComprehensiveResearchSection
                                                            research={TIME_TRACKER_DATA.research}
                                                        />
                                                        <ValidationResultsSection
                                                            validation={TIME_TRACKER_DATA.validation}
                                                        />
                                                    </div>

                                                    <CostAnalysisSection
                                                        costData={TIME_TRACKER_DATA.research.estimatedCost}
                                                        research={TIME_TRACKER_DATA.research}
                                                    />
                                                </div>
                                            )}

                                            {/* TAB 3: TECHNICAL */}
                                            {tourTab === 'technical' && (
                                                <div className="space-y-8 animate-fade-in">
                                                    <div className="text-center mb-6">
                                                        <Heading level={3} className="text-2xl mb-2 text-white">Full Stack Architecture</Heading>
                                                        <Text className="text-gray-400">A detailed breakdown of the generated codebase structure.</Text>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                        {TIME_TRACKER_DATA.technical.map((layer, idx) => (
                                                            <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-[#7B61FF]/50 transition-all group hover:-translate-y-1 duration-300">
                                                                <div className="p-5 border-b border-white/5 bg-gradient-to-br from-white/5 to-transparent">
                                                                    <div className="flex justify-between items-start mb-4">
                                                                        <div className="p-3 bg-black/40 rounded-xl text-[#7B61FF] group-hover:scale-110 transition-transform duration-300 border border-white/5 shadow-lg">
                                                                            <Icon name={layer.icon} size="md" />
                                                                        </div>
                                                                        <Badge variant="secondary" className="bg-black/40 backdrop-blur-md border-white/10">{layer.tech}</Badge>
                                                                    </div>
                                                                    <Heading level={3} className="text-xl font-bold text-white mb-1 group-hover:text-[#7B61FF] transition-colors">{layer.title}</Heading>
                                                                </div>

                                                                <div className="p-5 space-y-5">
                                                                    <Text className="text-sm text-gray-400 leading-relaxed">{layer.description}</Text>

                                                                    {/* Stats Grid */}
                                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                                        {layer.stats.map((stat, i) => (
                                                                            <div key={i} className="text-center bg-black/20 rounded-lg p-2 border border-white/5">
                                                                                <div className="text-white font-bold text-sm text-[#7B61FF]">{stat.value}</div>
                                                                                <div className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">{stat.label}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>

                                                                    {/* File Structure */}
                                                                    <div className="bg-black/30 rounded-xl p-3 border border-white/5">
                                                                        <div className="text-[10px] uppercase text-gray-500 font-bold mb-2 tracking-wider flex items-center">
                                                                            <Icon name="code" size="xs" className="mr-1" />
                                                                            Key Files
                                                                        </div>
                                                                        <div className="space-y-1.5">
                                                                            {layer.structure.map((file, i) => (
                                                                                <div key={i} className="flex items-start text-xs text-gray-300 font-mono group/file">
                                                                                    <span className="text-[#7B61FF] opacity-50 mr-2 shrink-0 group-hover/file:opacity-100 mt-0.5">/</span>
                                                                                    <span className="break-all">{file}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* TAB 4: VISUAL ARCHITECTURE (FLOW DIAGRAMS) */}
                                            {tourTab === 'flow' && (
                                                <div className="animate-fade-in">
                                                    <div className="text-center mb-8">
                                                        <Heading level={2} className="text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
                                                            System Flow & Architecture
                                                        </Heading>
                                                        <Text variant="secondary" className="max-w-2xl mx-auto">
                                                            Visualizing the complete user journey and system architecture
                                                        </Text>
                                                    </div>
                                                    <div className="bg-black/30 rounded-2xl border border-white/10 p-1">
                                                        <AppFlowDiagram diagramData={SAMPLE_DIAGRAM_DATA} />
                                                    </div>
                                                </div>
                                            )}

                                            {/* TAB 5: THE INSTRUCTIONS */}
                                            {tourTab === 'prompts' && (
                                                <div className="animate-fade-in space-y-4">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h3 className="text-white font-medium">Your Action Plan</h3>
                                                        <span className="text-xs text-gray-500">Copy these to your AI Coder</span>
                                                    </div>

                                                    {TIME_TRACKER_DATA.prompts.map((prompt) => (
                                                        <div key={prompt.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden group hover:border-[#7B61FF]/30 transition-all">
                                                            <div className="bg-white/5 p-4 border-b border-white/5 flex justify-between items-center">
                                                                <div className="flex items-center gap-3">
                                                                    <Badge variant="secondary" className="bg-[#7B61FF]/10 text-[#BA87FF] border-[#7B61FF]/20 text-xs">{prompt.tag}</Badge>
                                                                    <h4 className="text-white font-medium">{prompt.title}</h4>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleCopy(prompt.id)}
                                                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${copiedPrompt === prompt.id
                                                                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                                        : 'bg-[#7B61FF] text-white hover:bg-[#6b51eb]'
                                                                        }`}
                                                                >
                                                                    {copiedPrompt === prompt.id ? (
                                                                        <><Icon name="check" size="xs" /> Copied</>
                                                                    ) : (
                                                                        <><Icon name="copy" size="xs" /> Copy Prompt</>
                                                                    )}
                                                                </button>
                                                            </div>

                                                            <div className="p-5 space-y-6">
                                                                <div>
                                                                    <h5 className="text-[#BA87FF] text-xs uppercase tracking-wider font-semibold mb-2">Why This Matters</h5>
                                                                    <p className="text-gray-300 text-sm leading-relaxed">{prompt.whyMatters}</p>
                                                                </div>

                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                                    <div>
                                                                        <h5 className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-2">Expected Outputs</h5>
                                                                        <ul className="space-y-1">
                                                                            {prompt.expectedOutputs.map((item, i) => (
                                                                                <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                                                                                    <span className="w-1 h-1 rounded-full bg-[#7B61FF] mt-2 shrink-0"></span>
                                                                                    {item}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                    <div>
                                                                        <h5 className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-2">Success Validation</h5>
                                                                        <ul className="space-y-1">
                                                                            {prompt.successValidation.map((item, i) => (
                                                                                <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                                                                                    <span className="w-1 h-1 rounded-full bg-green-500/50 mt-2 shrink-0"></span>
                                                                                    {item}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>

                                                                <div className="bg-black/40 rounded-lg border border-white/5 p-3 font-mono text-xs text-gray-400">
                                                                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/5 text-gray-500">
                                                                        <Icon name="terminal" size="xs" />
                                                                        <span>Prompt Content</span>
                                                                    </div>
                                                                    <div className="text-gray-300 overflow-x-auto"><pre className="whitespace-pre-wrap break-all md:break-normal">{prompt.code}</pre></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* CTA Section */}
                        <section className="py-20 text-center relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#7B61FF]/10 blur-[80px] rounded-full pointer-events-none"></div>

                            <Heading level={2} className="mb-6 relative z-10">Ready to Architect Your Dream?</Heading>
                            <Text variant="secondary" size="lg" className="mb-10 max-w-2xl mx-auto z-10 relative">
                                Don't let your great idea get lost in bad code. Get the plan you need to build it right.
                            </Text>

                            <Button
                                onClick={() => navigate('/')}
                                className="bg-[#7B61FF] hover:bg-[#6B52E7] text-white px-10 py-4 rounded-xl text-lg font-bold shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_40px_rgba(123,97,255,0.6)] transition-all transform hover:scale-105 relative z-10"
                            >
                                Start Building for Free
                            </Button>
                        </section >

                    </Container >
                </main >

                <Footer />
            </div >
        </div >
    );
}